import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Calendar, Clock, MapPin, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [booking, setBooking] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const rentalId = searchParams.get('rental_id');
  const isRental = !!rentalId;

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    } else if ((bookingId || rentalId) && token) {
      fetchDetails();
    }
  }, [sessionId, bookingId, rentalId, token]);

  const pollPaymentStatus = async () => {
    if (pollCount >= 5) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          fetchDetails();
        } else if (data.status === 'expired') {
          setPaymentStatus('expired');
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  const fetchDetails = async () => {
    const id = bookingId || rentalId;
    const endpoint = isRental ? 'rentals' : 'bookings';
    if (!id) return;
    
    try {
      const response = await fetch(`${API}/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const items = await response.json();
        const item = isRental 
          ? items.find(r => r.rental_id === rentalId)
          : items.find(b => b.booking_id === bookingId);
        if (item) {
          setBooking(item);
          if (item.payment_status === 'paid') {
            setPaymentStatus('success');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching details:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="booking-success-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg"
        >
          <Card className="bg-card/50 backdrop-blur-md border-white/10 overflow-hidden">
            {paymentStatus === 'checking' && (
              <CardContent className="p-8 text-center">
                <div className="spinner mx-auto mb-4" style={{ width: 48, height: 48 }} />
                <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </CardContent>
            )}

            {paymentStatus === 'success' && (
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/20 flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-secondary" />
                </div>
                <h2 className="text-3xl font-bold mb-2 gradient-text">
                  {isRental ? 'Rental Confirmed!' : 'Booking Confirmed!'}
                </h2>
                <p className="text-muted-foreground mb-6">
                  Thank you for {isRental ? 'renting' : 'booking'} with Slither Sessions
                </p>

                {booking && (
                  <div className="bg-muted/50 rounded-lg p-4 text-left space-y-3 mb-6">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-3 text-primary" />
                      <span>{booking.date}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-3 text-primary" />
                      <span>{booking.start_time} ({booking.duration_hours}hr)</span>
                    </div>
                    {booking.location_type && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-3 text-primary" />
                        <span className="capitalize">{booking.location_type.replace('_', ' ')}</span>
                      </div>
                    )}
                    <div className="pt-3 border-t border-border">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Paid</span>
                        <span className="text-primary font-bold">${booking.total_price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full btn-primary"
                    data-testid="view-bookings-btn"
                  >
                    View My {isRental ? 'Rentals' : 'Bookings'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="w-full"
                    data-testid="back-home-btn"
                  >
                    Back to Home
                  </Button>
                </div>
              </CardContent>
            )}

            {paymentStatus === 'expired' && (
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-2 text-destructive">Session Expired</h2>
                <p className="text-muted-foreground mb-6">
                  Your payment session has expired. Please try again.
                </p>
                <Button onClick={() => navigate('/booking')} className="btn-primary">
                  Try Again
                </Button>
              </CardContent>
            )}

            {paymentStatus === 'timeout' && (
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-2">Payment Status Unknown</h2>
                <p className="text-muted-foreground mb-6">
                  We couldn't confirm your payment. Please check your email for confirmation.
                </p>
                <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                  View My Bookings
                </Button>
              </CardContent>
            )}

            {paymentStatus === 'error' && (
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-2 text-destructive">Error</h2>
                <p className="text-muted-foreground mb-6">
                  Something went wrong. Please contact support.
                </p>
                <Button onClick={() => navigate('/')} className="btn-primary">
                  Back to Home
                </Button>
              </CardContent>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
